/**
 * Type definitions for the application
 */

export const VoucherStatus = {
  ACTIVE: 'active',
  USED: 'used',
  EXPIRED: 'expired',
} as const;

export type VoucherStatus = typeof VoucherStatus[keyof typeof VoucherStatus];

export interface Voucher {
  id: string;
  brand_name: string;
  value: number;
  coupon_code: string;
  pin?: string;
  expiry_date?: string;
  gcid?: string;
  source_platform: string;
  purchase_date?: string;
  issuance_date?: string;
  status: VoucherStatus;
  used_date?: string;
  redemption_link?: string;
  claim_link?: string;
  notes?: string;
  message_id?: string;
  created_at: string;
  updated_at: string;
}

export interface VoucherCreate {
  brand_name: string;
  value: number;
  coupon_code: string;
  pin?: string;
  expiry_date?: string;
  gcid?: string;
  source_platform: string;
  purchase_date?: string;
  redemption_link?: string;
  notes?: string;
  status?: VoucherStatus;
  message_id?: string;
}

export interface VoucherUpdate {
  brand_name?: string;
  value?: number;
  coupon_code?: string;
  pin?: string;
  expiry_date?: string;
  gcid?: string;
  source_platform?: string;
  purchase_date?: string;
  redemption_link?: string;
  notes?: string;
  status?: VoucherStatus;
  used_date?: string;
  message_id?: string;
}

export interface VoucherListResponse {
  vouchers: Voucher[];
  total: number;
  page: number;
  page_size: number;
  total_pages: number;
}

export interface VoucherFilter {
  brand_name?: string;
  status?: VoucherStatus;
  source_platform?: string;
  expiry_date_from?: string;
  expiry_date_to?: string;
  value_min?: number;
  value_max?: number;
  search?: string;
  sort_by?: 'expiry_date' | 'value' | 'brand_name' | 'purchase_date';
  sort_order?: 'asc' | 'desc';
}

export interface VoucherStatistics {
  total_vouchers: number;
  total_value: number;
  active_count: number;
  active_value: number;
  used_count: number;
  used_value: number;
  expired_count: number;
  expired_value: number;
  by_brand: Record<string, { count: number; value: number }>;
  by_platform: Record<string, { count: number; value: number }>;
  expiring_soon: number;
}

export interface User {
  user_id: string;
  email?: string;
  [key: string]: unknown;
}

export interface AuthState {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
}

// Gmail Connection Types
export const GmailConnectionStatus = {
  PENDING_AUTHORIZATION: 'PENDING_AUTHORIZATION',
  LABEL_PENDING: 'LABEL_PENDING',
  ACTIVE: 'ACTIVE',
  INACTIVE: 'INACTIVE',
  EXPIRED: 'EXPIRED',
  ERROR: 'ERROR',
} as const;

export type GmailConnectionStatus = typeof GmailConnectionStatus[keyof typeof GmailConnectionStatus];

export interface GmailConnection {
  connection_id: string;
  identifier: string;
  connection_name: string;
  label_id?: string;
  label_name?: string;
  user_info?: {
    email: string;
    name?: string;
    picture?: string;
    verified_email?: boolean;
    [key: string]: unknown;
  };
  authorization_url?: string;
  status: GmailConnectionStatus;
  watch_history_id?: string;
  watch_expiration?: string;
  last_sync_at?: string;
  last_error?: string;
  created_at: string;
  updated_at: string;
  created_by: string;
}

// eslint-disable-next-line @typescript-eslint/no-empty-object-type -- API expects empty body
export interface GmailConnectionCreate {}

export interface GmailConnectionUpdate {
  label_id: string;
  label_name?: string;
}

export interface GmailLabel {
  id: string;
  name: string;
  type: 'user' | 'system';
}

